import {Box, HStack, Text} from '@chakra-ui/react';
import {useHalCollection, useHalState} from '@synako/haligator';
import {
  adresseDeLaLivraisonSelectionnee,
  useLivraisonsPossiblesContext,
  usePanier,
} from 'ff-common';
import {useTranslation} from 'react-i18next';
import {useClient} from '@/haligator-factory';

/**
 * Pastille « raison sociale · ville » du bloc d'accueil : rappelle pour quel point de livraison
 * le client commande. Posée sur la carte d'accueil en version ordinateur, sur la photo
 * d'ambiance en version mobile.
 */

// Point médian entouré d'espaces insécables : le séparateur ne passe jamais seul à la ligne
const SEPARATEUR = ' · ';

const STYLES_PAR_VARIANTE = {
  carte: {
    h: '26px',
    pl: '10px',
    pr: '12px',
    borderRadius: 'full',
    bg: 'gray.pill_bg',
    color: 'gray.dark',
  },
  image: {
    h: '32px',
    px: '13px',
    borderRadius: '15px',
    bg: 'white.transparency_20',
    border: '1px solid',
    borderColor: 'white.transparency_30',
    backdropFilter: 'blur(8px)',
    color: 'white',
  },
};

export const AccueilPastilleClient: React.FC<{variante: 'carte' | 'image'}> = ({
  variante,
}) => {
  const {t} = useTranslation();
  const client = useClient();
  const {data} = useHalState(client);
  const adresses = useHalCollection(client.follow('adresses_de_livraison'));
  const {date_de_livraison} = useLivraisonsPossiblesContext();
  const {data: panier} = useHalState(usePanier());

  if (!data) {
    return null;
  }

  const adresse = adresseDeLaLivraisonSelectionnee({
    adresses: adresses.items?.map((item) => item.data),
    panier,
    date_de_livraison,
  });
  const ville = adresse?.tolede.adresse.ville;
  const raisonSociale = data.tolede.raison_sociale ?? data.tolede.code_client;
  const texteComplet = ville
    ? `${raisonSociale}${SEPARATEUR}${ville}`
    : raisonSociale;

  return (
    <HStack
      role='group'
      aria-label={
        ville
          ? t('accueil.pastille_client.aria_label', {
              raison_sociale: raisonSociale,
              ville,
            })
          : t('accueil.pastille_client.aria_label_sans_ville', {
              raison_sociale: raisonSociale,
            })
      }
      title={texteComplet}
      spacing='8px'
      minW={0}
      maxW='100%'
      flexShrink={1}
      fontSize='xs'
      fontWeight='600'
      lineHeight='15px'
      {...STYLES_PAR_VARIANTE[variante]}
    >
      {variante === 'carte' && (
        <Box
          aria-hidden
          flexShrink={0}
          boxSize='6px'
          borderRadius='full'
          bg='blue.main'
        />
      )}
      <HStack
        aria-hidden
        spacing={0}
        minW={0}
        whiteSpace='nowrap'
      >
        <Text
          as='span'
          minW={0}
          overflow='hidden'
          textOverflow='ellipsis'
        >
          {raisonSociale}
        </Text>
        {ville && (
          <>
            <Text
              as='span'
              flexShrink={0}
            >
              {SEPARATEUR}
            </Text>
            {/* La ville rétrécit en premier : son facteur de rétrécissement écrase celui de la raison sociale */}
            <Text
              as='span'
              flexShrink={1000}
              minW='4ch'
              overflow='hidden'
              textOverflow='ellipsis'
            >
              {ville}
            </Text>
          </>
        )}
      </HStack>
    </HStack>
  );
};
